/*
A block-level typography property has to WIN over the inline declaration it
duplicates. This module is how.

THE BUG THIS EXISTS FOR. A converted text block carries its own typography
inline, on the paragraph:

    <p style="margin:0;text-align:center;font-size:16px;color:#101b24">Legal</p>

`renderText` wraps that in a container carrying the block's props:

    <div style="font-family:…;font-size:11px;line-height:24px;color:#ff0000;text-align:right;">
      <p style="margin:0;text-align:center;font-size:16px;color:#101b24">Legal</p>
    </div>

The declaration on the child wins in every browser and every mail client, so
Align, Size, Line height, Colour and Font in the properties panel did nothing at
all for any converted block — not just on the canvas, in the SENT EMAIL. The
panel was lying about what it controlled.

THE FIX, AND ITS TWO RULES.

  1. It happens AT RENDER TIME ONLY. `block.html` in the document is never
     rewritten. Clearing a property has to restore exactly what the paragraph
     had, and the stored HTML is also what the conversion probe's `visibleText`
     check compares — so the stored string stays byte-for-byte what was saved.

  2. It lives in ONE place, used by both `renderBlocks` (the email) and
     `previewOf` (the canvas), so the two cannot disagree about what an override
     rewrites. That is the same reason `clamp`, `align` and `GUTTER` are shared.

An UNSET property changes nothing. That is the load-bearing property of this
module: every one of the 78 live templates converts losslessly today, and a
converted text block whose wrapper carried no font-size / line-height / colour /
family gets '' for all four — so it renders byte-identically to before this
change. The tests hammer that case hardest.

A SET property does NOT delete the declaration it duplicates, it OVERWRITES it
with the container's own value. Deleting is not inheriting — see
`overriddenDeclarations` for the <h1> that made a 18px block render at 36px.

A NOTE ON `align`, WHICH IS NEVER UNSET. `text` and `heading` blocks always have
an alignment (the converter reads one off the wrapper and falls back to 'left'),
so `text-align` is always overridden. Where a block's paragraphs disagreed with
each other about alignment, the block's alignment now wins for all of them. That
is a real flattening and it is deliberate: one block, one alignment is what the
properties panel offers, and the alternative is a panel control that silently
does nothing.

CommonJS, no DOM: it runs under plain `node src/utils/emailTypographyOverride.test.js`
and inside the serialiser, which is pure by contract.
*/

// The whole map, and deliberately only this. Each entry is "a block property
// that duplicates an inline declaration", so setting the property has to
// overwrite that declaration in the content it wraps.
//
// `color` is the delicate one. A naive substring match on 'color' also hits
// `background-color`, and losing a highlight — `<span style="background-color:#8cdc83">`
// is a real thing in this corpus — is a visible change to somebody's copy, not a
// no-op. Matching is on the WHOLE property name, so `background-color`,
// `border-color` and `mso-line-height-alt` are all left alone.
const TYPOGRAPHY_OVERRIDES = [
  { prop: 'align', declaration: 'text-align', kind: 'align' },
  { prop: 'fontSize', declaration: 'font-size', kind: 'number' },
  { prop: 'lineHeight', declaration: 'line-height', kind: 'number' },
  { prop: 'color', declaration: 'color', kind: 'string' },
  { prop: 'fontFamily', declaration: 'font-family', kind: 'string' },
];

// Restated here rather than imported from ./emailBlocks: that module requires
// this one, and a require cycle between the serialiser and its helper is a class
// of bug nobody should have to debug at 5pm. Three values that have not changed
// since the block model existed.
const ALIGNMENTS = ['left', 'center', 'right'];

/*
Is this property SET — i.e. does the container actually emit a declaration of its
own for it?

The three kinds mirror how the serialiser reads each prop:

  align   — one of the three alignments, anything else falls back and is not an
            override of the user's making;
  number  — '' is a number field the user has just cleared to retype, and a
            non-finite value is not a size; both mean "the document's";
  string  — a colour or a font stack, empty meaning "the document's" exactly as
            `typeFace` reads it.
*/
function isSet(kind, value) {
  if (kind === 'align') return ALIGNMENTS.indexOf(value) !== -1;
  if (kind === 'number') {
    if (value === '' || value === null || value === undefined) return false;
    return Number.isFinite(Number(value));
  }
  return typeof value === 'string' && value.trim() !== '';
}

// The value the container itself emits for this property, so a descendant can be
// REWRITTEN to it rather than left with nothing. The `px` suffixes mirror
// `renderText`/`renderHeading`, which emit both numbers in px.
function forcedValue(entry, block) {
  const raw = block[entry.prop];
  if (entry.declaration === 'font-size') return `${Number(raw)}px`;
  if (entry.declaration === 'line-height') return `${Number(raw)}px`;
  return String(raw).trim();
}

/*
The declarations a block's own properties override, in map order, as
{declaration, value}. Empty for a block that sets nothing, which is what makes
"an unset property changes nothing" true.

WHY A VALUE AND NOT JUST A NAME. Removing the declaration outright looks
equivalent and is not: an element with a user-agent default of its own falls back
to THAT, not to the container. This corpus marks body copy up as <h1> — the
presale templates do it — and an <h1> whose font-size is deleted renders at the
UA's 2em, so setting the block to 18px produced 36px text. It only looked right
while the block was selected, because the editor forces inheritance.

`inherit` would fix the cascade but not the email: Outlook's Word engine does not
honour it reliably, which would leave the same bug in the client that matters
most. So the declaration is rewritten to the container's own literal value.
*/
function overriddenDeclarations(block) {
  if (!block || typeof block !== 'object') return [];
  return TYPOGRAPHY_OVERRIDES
    .filter(entry => isSet(entry.kind, block[entry.prop]))
    .map(entry => ({ declaration: entry.declaration, value: forcedValue(entry, block) }));
}

/*
One style attribute's declarations, with the overridden ones rewritten to the
container's values.

Split on ';' and matched by whole property name, so the parts that are NOT
rewritten keep their original spacing character for character — which is what
lets the caller detect "nothing changed" by string equality and hand back the
input untouched.

A value containing a ';' (inside a url() or a quoted font name) would be split
wrongly. No declaration in this corpus has one, and the failure mode is a
surviving fragment rather than a lost declaration, so it is not worth a CSS
parser here.
*/
function filterDeclarations(css, declarations) {
  // Rewritten, not removed — see overriddenDeclarations for why deleting a
  // font-size from an <h1> makes it BIGGER rather than making it inherit.
  const rewritten = css
    .split(';')
    .map((part) => {
      const colon = part.indexOf(':');
      if (colon === -1) return part;
      const name = part.slice(0, colon).trim().toLowerCase();
      const hit = declarations.find(entry => entry.declaration === name);
      if (!hit) return part;
      // Keep the original leading whitespace so an untouched attribute still
      // compares equal character for character.
      const lead = part.slice(0, part.length - part.trimStart().length);
      return `${lead}${name}:${hit.value}`;
    })
    .join(';');
  return rewritten;
}

// Every tag in the string. Attribute values holding a '>' would break this, and
// none in this corpus does; the important property is the opposite one — the
// CONTENTS of a <style> element are not a tag, so a rule written in a <style>
// block is never touched. Only style ATTRIBUTES are.
const TAG = /<[a-zA-Z][^>]*>/g;

// The style attribute inside one tag, single- or double-quoted. The leading
// whitespace is part of the match so an attribute that empties out can be
// removed without leaving a double space behind.
const STYLE_ATTR = /(\s+)style\s*=\s*(["'])([\s\S]*?)\2/gi;

/*
`html` with every declaration the block overrides rewritten, in its descendants'
style attributes, to the value the container emits for it.

Returns the input string identically when the block overrides nothing, or when
nothing in the string matched — so a document that sets no typography renders
byte-for-byte as it did before this module existed.
*/
function stripOverriddenDeclarations(html, block) {
  if (html === null || html === undefined) return '';
  const source = String(html);
  const declarations = overriddenDeclarations(block);
  if (declarations.length === 0 || source === '') return source;

  return source.replace(TAG, tag => tag.replace(STYLE_ATTR, (whole, space, quote, css) => {
    const next = filterDeclarations(css, declarations);
    if (next === css) return whole;
    // Kept as a guard, though rewriting cannot reach it: a matched declaration
    // is replaced, never dropped, so an attribute that changed at all still
    // holds something. If it ever did empty out, dropping the attribute — the
    // leading whitespace with it — beats leaving `style=""` and `<p  >`.
    if (next.trim() === '') return '';
    return `${space}style=${quote}${next}${quote}`;
  }));
}

/*
The class names the CANVAS's editing surface needs.

The canvas draws a selected text block through Tiptap, and Tiptap edits the
STORED html — unstripped, because what it hands back on the next keystroke is
what gets saved, and saving a stripped paragraph would destroy the very
declarations rule 1 above promises to keep. So while the caret is in the block
the inline declarations are still on the DOM node and would still win.

These classes let emails.css neutralise them for display only — one rule per
override, `inherit !important` on elements carrying a style attribute, which is
exactly the set of elements `stripOverriddenDeclarations` edits. `inherit` is
right HERE and wrong in the email: on the canvas it resolves against the same
container whose value the rewrite writes out literally, so selected and
deselected agree, while in Outlook it would not resolve at all. Nothing is
written anywhere; the moment you click away the block renders from the rewritten
string and the two agree.
*/
const CLASS_FOR_DECLARATION = {
  'text-align': 'is-ovr-align',
  'font-size': 'is-ovr-size',
  'line-height': 'is-ovr-leading',
  color: 'is-ovr-color',
  'font-family': 'is-ovr-font',
};

function overrideClassName(block) {
  return overriddenDeclarations(block)
    .map(entry => CLASS_FOR_DECLARATION[entry.declaration])
    .filter(Boolean)
    .join(' ');
}

module.exports = {
  TYPOGRAPHY_OVERRIDES,
  CLASS_FOR_DECLARATION,
  overriddenDeclarations,
  stripOverriddenDeclarations,
  overrideClassName,
};
